import { FC, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import Swiper from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import useClassNames from 'helpers/hooks/useClassNames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';
import { ProductDetailsProps } from '../product/product-details';
import debounce from 'lodash.debounce';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import { useFormat } from 'helpers/hooks/useFormat';
import { trackEvent } from 'helpers/customEvents';

declare global {
  interface Window {
    ConfigureUI?: any;
  }
}

interface GalleryProps {
  images: Array<string>;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  setLoading: (value: boolean) => void;
  sku: string;
  color: string;
  showRiviereBadge?: boolean;
  onRiviereBadgeClick?: () => void;
}

const Gallery: FC<GalleryProps> = ({
  images,
  inModalVersion,
  setLoading,
  sku,
  color,
  showRiviereBadge,
  onRiviereBadgeClick,
}) => {
  const swiperRef = useRef<Swiper>();
  const [activeSlide, setActiveSlide] = useState(0);
  const width = useWindowWidth();
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isImageError, setIsImageError] = useState(true);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const imagesToDisplay = images.length === 1 ? [...images, ...images] : images;
  const [transitionDuration, setTransitionDuration] = useState(1000);

  const slideTo = (slide: number) => {
    swiperRef.current?.slideTo(slide);
  };

  const clearLoading = useCallback(
    debounce(() => setLoading(false), 2000),
    [setLoading],
  );

  useEffect(() => {
    clearLoading();
  }, [clearLoading]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://inhaabitcdn.com/configure-ui/v4/ConfigureUI.min.js';
    script.async = true;
    script.onload = () => {
      setIsConfigured(true);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const slideToActive = (slideIndex: number) => {
    swiperRef.current?.slideTo(slideIndex);
  };

  useEffect(() => {
    slideToActive(activeSlide);
  }, [activeSlide]);

  const reinitializeConfigureUI = useCallback(() => {
    if (isConfigured && swiperRef.current) {
      const applyToAll = images.length === 1;
      swiperRef.current.slides.forEach((slide, index) => {
        if (applyToAll || index % 2 === 0) {
          new window.ConfigureUI({
            id: '64180e9626eee11f02dac5b7',
            mode: 'lightbox',
            buttonPosition: 'inline',
            sku: `${sku}*${color}`,
            element: slide,
          });
        }
      });
    }
  }, [isConfigured, images, sku, color]);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.slides) {
      swiperRef.current.slides.forEach((slide) => {
        const existingButton = slide.querySelector('#configure-button');
        if (existingButton) {
          existingButton.remove();
        }
      });
      reinitializeConfigureUI();
    }
  }, [reinitializeConfigureUI, sku, color, images.length]);

  const imagesContainerClassName = useClassNames([
    'relative ml-20 lg:mx-auto',
    inModalVersion ? 'h-[250px] max-w-[300px]' : 'h-full',
  ]);

  const clearImage = useCallback(
    debounce(() => setLoading(false), 750),
    [setLoading],
  );

  useEffect(() => {
    clearImage();
  }, [clearImage]);

  return (
    <div className="relative w-full gap-y-34 px-24 md:px-28 lg:px-28 xl:px-30 2xl:px-30 mt-[4rem]">
      {imagesToDisplay.length > 0 ? (
        <Slider
          arrows={isTabletSize ? true : false}
          dots={isTabletSize ? false : true}
          loop
          speed={transitionDuration}
          prevButtonStyles={{
            left: isDesktopSize ? -30 : -10,
            padding: isDesktopSize ? '5px' : '10px',
            zIndex: 10,
          }}
          nextButtonStyles={{
            right: isDesktopSize ? -30 : -10,
            padding: isDesktopSize ? '5px' : '10px',
            zIndex: 10,
          }}
          slidesPerView={isTabletSize ? 2 : 1.25}
          spaceBetween={8}
          allowArrowsOnTouchDevice={true}
          autoplay={true}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {imagesToDisplay.map((image, index) => (
            <div
              key={index}
              className={`relative ${index === activeSlide ? 'ps-slider-for' : ''}`}
              onClick={() => trackEvent('carousel_interaction', {
                action_type: 'image_click',
                slide_index: index,
              })}
            >
              <div className="relative">
                <Image
                  priority
                  src={encodeURI(image)}
                  alt={image}
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '650px',
                    objectFit: 'cover',
                  }}
                  width={500}
                  height={300}
                  onLoad={() => setLoading(false)}
                  onError={(event: any) => {
                    event.target.src = '/images/no-image-placeholder/product-thumbnail.jpg';
                    event.target.srcset = '/images/no-image-placeholder/product-thumbnail.jpg';
                  }}
                />
                <script className="p-20" src="https://inhaabitcdn.com/configure-ui/v4/ConfigureUI.min.js" async />
              </div>
            </div>
          ))}
        </Slider>
      ) : (
        <Image
          priority
          src={'/images/no-image-placeholder/product-thumbnail.jpg'}
          alt={'No Image found'}
          sizes="100vw"
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: '390px',
            objectFit: 'contain',
          }}
          width={500}
          height={300}
          onLoad={() => setLoading(false)}
        />
      )}
    </div>
  );
};

export default Gallery;
